// Several products on one cargo deal (go-live card 1248h535zfw, Chris's
// walk-through 2026-10-20). A Bid/Offer or recap holds either one product in
// its header (as before) or two or more product lines. With lines, the SERVER
// works out the header from them on every save (backend/offer/product_lines.py):
// first product, total quantity, quantity-weighted average price. These
// helpers show the same total and average on screen while someone types,
// shape the lines for a save, and carry a bid's lines into its recap.
//
// One currency and one unit per deal (Chris's call): a line has neither; the
// deal's own currency and unit apply to every line.
//
// Hand-ported from HMS. On IMT a deal's NAME is the server's
// (backend/offer/deal_names.py: "RB1 +1 - 10k - ..."), so HMS's screen-side
// list naming (summaryFromLines / severalProductsPart) is not carried here.
import { addFigures, formatFigure, multiplyRound, subtractFigures, PRICE_DECIMALS, QUANTITY_DECIMALS } from './precision';
import { canonicalizeAmount } from './amountFormat';

// Which keys a line uses. A Bid/Offer line has `quantity` and `price`. A recap
// line has `sale_quantity` (this leg's quantity on both legs) and this leg's
// own price: `sale_price` on a sale, `buy_price` on a purchase -- the bunker
// recap convention (TradeRecap.js buildRecapPayload).
export const OFFER_LINE_KEYS = { quantityKey: 'quantity', priceKey: 'price' };
export const recapLineKeys = (recapType) => ({
  quantityKey: 'sale_quantity',
  priceKey: String(recapType || '').toUpperCase() === 'PURCHASE' ? 'buy_price' : 'sale_price',
});

// Traders type "6 000" as often as "6,000": both read as 6000 (the server
// reads them the same way).
const figure = (value) => {
  if (value === null || value === undefined) return '';
  return canonicalizeAmount(String(value).replace(/\s/g, ''));
};

// The product picker hands back "Steam Coal | South Africa | ... - RB1"; a
// deal shows the part before the first "|" -- the same name the single
// Product box gets (OfferGenerator applyVariantToProductField) and the server
// gives a line (offer/product_lines.py catalogue_product_label).
export const productNameFromPicker = (displayName) => (
  String(displayName || '').split('|')[0].trim() || String(displayName || '').trim()
);

// A product line's name, e.g. "Steam Coal - RB1": the picker's name plus the
// product's own short name, because every coal is "Steam Coal" before the
// first "|". Server twin: catalogue_product_label in backend/offer/product_lines.py.
const PLACEHOLDER_SHORTNAME = 'Variant';
export const lineProductLabel = (displayName, variant) => {
  const name = productNameFromPicker(displayName);
  const short = String(variant?.shortname || '').trim();
  if (!variant || variant.category === 'lubrication' || !short
      || short === PLACEHOLDER_SHORTNAME || name.endsWith(short)) return name;
  return `${name} - ${short}`;
};

// A line's key on screen, made up when the line is added and kept after a
// save gives it an id, so its box keeps focus. Unique across page loads too:
// a restored backup brings its lines' keys back (review rc013da).
let nextLineKey = 0;
const newLineKey = () => {
  nextLineKey += 1;
  return `line-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}-${nextLineKey}`;
};

export const blankLine = ({ quantityKey, priceKey }) => ({
  _key: newLineKey(),
  id: null,
  stock_item_variant: null,
  product_label: '',
  [quantityKey]: '',
  [priceKey]: '',
});

// Lines as the server sent them -> lines the table edits.
export const linesFromServer = (products) => (
  (Array.isArray(products) ? products : []).map((row) => ({
    ...row,
    product_label: row.product_label || '',
  }))
);

// Total and average while typing. Each is '' until every line can answer:
// a half-typed line means no honest total yet (the server says the same).
export const linesHeader = (lines, { quantityKey, priceKey }) => {
  const rows = Array.isArray(lines) ? lines : [];
  if (rows.length === 0) return { total: '', average: '' };
  const quantities = rows.map((row) => figure(row[quantityKey]));
  if (quantities.some((q) => q === '')) return { total: '', average: '' };
  const total = quantities.reduce((sum, q) => addFigures(sum, q, QUANTITY_DECIMALS), '0');
  const prices = rows.map((row) => figure(row[priceKey]));
  if (!(parseFloat(total) > 0) || prices.some((p) => p === '')) {
    return { total: formatFigure(total, 0, QUANTITY_DECIMALS), average: '' };
  }
  const value = quantities.reduce(
    (sum, q, i) => addFigures(sum, multiplyRound(q, prices[i], PRICE_DECIMALS + QUANTITY_DECIMALS), PRICE_DECIMALS + QUANTITY_DECIMALS),
    '0',
  );
  return {
    total: formatFigure(total, 0, QUANTITY_DECIMALS),
    average: formatFigure(parseFloat(value) / parseFloat(total), 0, PRICE_DECIMALS),
  };
};

// What each line still lacks, in screen words: ["Product 2: price"].
// `priceRequired` false: the leg is priced by its formula (one product needs
// a price OR a formula), so a line needs no figure.
export const missingOnLines = (lines, { quantityKey, priceKey }, { priceRequired = true } = {}) => {
  const missing = [];
  (lines || []).forEach((row, index) => {
    const parts = [];
    if (!row.stock_item_variant) parts.push('product');
    if (!(parseFloat(figure(row[quantityKey])) > 0)) parts.push('quantity');
    if (priceRequired && figure(row[priceKey]) === '') parts.push('price');
    if (parts.length) missing.push(`Product ${index + 1}: ${parts.join(', ')}`);
  });
  return missing;
};

// Lines shaped for a save: display-only keys dropped, empty boxes sent as
// empty (null), and a line not yet saved sent without an id so it is created.
const DISPLAY_ONLY = ['_key', 'product_label', 'stock_item_variant_data', 'stock_item_variant_display', 'supplier_name'];
export const linesForSave = (lines) => (
  (Array.isArray(lines) ? lines : []).map((row) => {
    const copy = { ...row };
    DISPLAY_ONLY.forEach((key) => delete copy[key]);
    Object.keys(copy).forEach((key) => {
      if (copy[key] === '') copy[key] = null;
    });
    if (copy.id == null) delete copy.id;
    return copy;
  })
);

// How much of each bid line is still free: the line's quantity minus what
// earlier recaps of this bid already took of the same product (`taken` is a
// list of {stock_item_variant, quantity}). Only a starting suggestion for the
// recap pop-up -- the bid itself closes on its total (Chris's call).
export const remainingPerLine = (offerLines, taken) => {
  const left = new Map();
  (taken || []).forEach(({ stock_item_variant: variant, quantity }) => {
    const key = String(variant);
    left.set(key, addFigures(left.get(key) || '0', figure(quantity) || '0', QUANTITY_DECIMALS));
  });
  return (offerLines || []).map((line) => {
    const key = String(line.stock_item_variant);
    const already = left.get(key) || '0';
    const own = figure(line.quantity) || '0';
    const free = subtractFigures(own, already, QUANTITY_DECIMALS);
    // What this line could not absorb is left for a later line of the same product.
    left.set(key, parseFloat(free) < 0 ? subtractFigures('0', free, QUANTITY_DECIMALS) : '0');
    return parseFloat(free) > 0 ? formatFigure(free, 0, QUANTITY_DECIMALS) : '0';
  });
};

// What the bid's earlier recaps already took, product by product, for
// remainingPerLine. A recap that took one product has no lines (one product
// lives in the header), so its header product and quantity count too.
export const takenByRecaps = (recaps) => (recaps || []).flatMap((recap) => {
  const lines = Array.isArray(recap.products) ? recap.products : [];
  if (lines.length) {
    return lines.map((line) => ({ stock_item_variant: line.stock_item_variant, quantity: line.sale_quantity }));
  }
  const purchase = String(recap.recap_type || '').toUpperCase() === 'PURCHASE';
  const quantity = purchase ? recap.purchase_contract_quantity : recap.sale_contract_quantity;
  return recap.stock_item_variant ? [{ stock_item_variant: recap.stock_item_variant, quantity }] : [];
});

// A bid/offer's lines -> the recap's lines, each with the quantity the
// trader chose in the pop-up (lines left at 0 are not taken) and the bid's
// price for that line in this leg's price key.
export const recapLinesFromOffer = (offerLines, quantities, recapType) => {
  const { priceKey } = recapLineKeys(recapType);
  return (offerLines || [])
    .map((line, index) => ({ line, quantity: figure(quantities[index]) }))
    .filter(({ quantity }) => parseFloat(quantity) > 0)
    .map(({ line, quantity }) => ({
      stock_item_variant: line.stock_item_variant,
      sale_quantity: quantity,
      [priceKey]: figure(line.price) || null,
    }));
};

// A deal's lines as a screen holds them: two or more, or none (one product
// lives in the header, as before).
export const severalLines = (products) => {
  const lines = linesFromServer(products);
  return lines.length >= 2 ? lines : [];
};

// After a save, lines that had no id yet take the id the server gave them,
// so the next save updates them instead of making new ones. Matched by
// position; anything typed meanwhile is kept.
export const adoptSavedIds = (current, saved) => {
  const rows = Array.isArray(current) ? current : [];
  const savedRows = Array.isArray(saved) ? saved : [];
  if (rows.length === 0 || rows.length !== savedRows.length) return rows;
  if (rows.every((row) => row.id != null)) return rows;
  // Never hand out an id a row already holds: lines changed while the save
  // was on its way would otherwise end up sharing one.
  const held = new Set(rows.filter((row) => row.id != null).map((row) => String(row.id)));
  return rows.map((row, i) => {
    const id = savedRows[i]?.id;
    if (row.id != null || id == null || held.has(String(id))) return row;
    held.add(String(id));
    return { ...row, id };
  });
};

// What the leave check compares: product, quantity and price of each line.
export const linesSignature = (lines, { quantityKey, priceKey }) => JSON.stringify(
  (Array.isArray(lines) ? lines : []).map((row) => [
    String(row.stock_item_variant ?? ''),
    formatFigure(figure(row[quantityKey]), 0, QUANTITY_DECIMALS),
    formatFigure(figure(row[priceKey]), 0, PRICE_DECIMALS),
  ]),
);

// The offer text (the wording previews the Bid/Offer saves and sends) for a
// deal with several products: the Product & Origin clause prints a product
// table, and Quantity and Base Price point at it (Chris's walk-through step 4).
// Returns null for a clause these lines don't change, and for one product.
//   ctx: { lines, origin, unit, currency, priceType,
//          tolerance, option, incoterm, portType, cvBasis, kcal, indexPeriod }
export const severalProductsClauseText = (clauseName, ctx) => {
  const lines = Array.isArray(ctx.lines) ? ctx.lines : [];
  if (lines.length < 2) return null;
  const unit = ctx.unit || '';
  const isFixed = ctx.priceType === 'Fix Price';
  const priceText = (raw) => {
    const value = figure(raw);
    if (value === '') return '';
    if (isFixed) return `${ctx.currency || ''} ${formatFigure(value, 2, PRICE_DECIMALS)}`.trim();
    const n = parseFloat(value);
    return `${ctx.priceType || 'index'} ${n < 0 ? 'minus' : 'plus'} ${ctx.currency || ''} ${formatFigure(String(Math.abs(n)), 2, PRICE_DECIMALS)}`.replace(/\s+/g, ' ').trim();
  };
  if (clauseName === 'Commodity') {
    const head = ctx.origin
      ? `Product & Origin: the products below, in bulk of ${ctx.origin} Origin`
      : 'Product & Origin: the products below';
    const rows = lines.map((line, i) => {
      const qty = formatFigure(figure(line.quantity), 0, QUANTITY_DECIMALS);
      const price = priceText(line.price);
      const qtyPart = qty ? ` ${qty}${unit ? ` ${unit}` : ''}` : '';
      const pricePart = price ? ` at ${price}${unit ? ` per ${unit}` : ''}` : '';
      return `${i + 1}. ${line.product_label || 'Product'}:${qtyPart}${pricePart}`;
    });
    // Additional information is added after this by the caller, as it is
    // for one product.
    return [head, ...rows].join('\n');
  }
  if (clauseName === 'Quantity') {
    const { total } = linesHeader(lines, OFFER_LINE_KEYS);
    let text = `Quantity: ${total || ''}${unit ? ` ${unit}` : ''} in total, per product as listed`;
    if (ctx.tolerance) text += ` with +/-${ctx.tolerance}% tolerance`;
    if (ctx.option) text += ` in ${String(ctx.option).replace('Quantity in ', '')}`;
    return text;
  }
  if (clauseName === 'Base Price') {
    let text = `Base Price: per product as listed, per ${unit || 'metric ton'} ${ctx.incoterm || ''} ${ctx.portType || ''}`.replace(/\s+/g, ' ').trim();
    if (ctx.kcal) text += ` ${ctx.cvBasis || ''} ${ctx.kcal} kcal/kg`;
    if (!isFixed && ctx.indexPeriod) {
      text += `\nApplicable Index Period: ${String(ctx.indexPeriod).replace('[INDEX]', ctx.priceType)}`;
    }
    return text;
  }
  return null;
};

// ---- The Trade Recap's form -----------------------------------------------

const legOf = (recapType) => (String(recapType || '').toUpperCase() === 'PURCHASE' ? 'purchase' : 'sale');

// The recap form after its lines change. Two or more lines fill this leg's
// Product, Quantity and Price boxes (first product, total, average) the way
// the server will store them; one line left goes back to the single boxes.
export const recapFieldsWithLines = (fields, next, recapType) => {
  const keys = recapLineKeys(recapType);
  const leg = legOf(recapType);
  if (next.length === 1) {
    const only = next[0];
    return {
      ...fields,
      products: [],
      stock_item_variant: only.stock_item_variant ?? null,
      product: only.product_label || '',
      product_label: only.product_label || '',
      [`${leg}_contract_quantity`]: figure(only[keys.quantityKey]),
      [`${leg}_contract_price_value`]: figure(only[keys.priceKey]),
    };
  }
  const head = next[0] || {};
  const { total, average } = linesHeader(next, keys);
  return {
    ...fields,
    products: next,
    stock_item_variant: head.stock_item_variant ?? null,
    product: head.product_label || '',
    product_label: head.product_label || '',
    [`${leg}_contract_quantity`]: total,
    [`${leg}_contract_price_value`]: average,
  };
};

// "+ Add another product" on a recap: its one product becomes line 1.
export const recapLinesFromHeader = (fields, recapType) => {
  const keys = recapLineKeys(recapType);
  const leg = legOf(recapType);
  return [
    {
      ...blankLine(keys),
      stock_item_variant: fields.stock_item_variant ?? null,
      // The deal's catalogue product name (server: catalogue_product_label),
      // else the Product text of a deal with no catalogue product.
      product_label: fields.product_label || fields.product || '',
      [keys.quantityKey]: figure(fields[`${leg}_contract_quantity`]),
      [keys.priceKey]: figure(fields[`${leg}_contract_price_value`]),
    },
    blankLine(keys),
  ];
};

// A cargo recap's lines as its save sends them: two or more, or none (one
// product lives in the header, as before, so a single left-over line goes).
export const cargoRecapLinesForSave = (products) => (
  Array.isArray(products) && products.length >= 2 ? linesForSave(products) : []
);

// A cargo recap with several products (go-live 1248h535zfw): its lines, or
// the lists' summary, say so.
export const hasSeveralProducts = (recap) => (
  recap?.commodity_type !== 'fuels'
  && ((Array.isArray(recap?.recap_products) && recap.recap_products.length >= 2)
    || (recap?.product_lines_summary?.count || 0) >= 2)
);
